/** 轿厢状态机（设计文档 entities/elevator-car.ts）。 */
import type { ElevatorConfig } from '../config-schema';

export type CarState = 'idle' | 'moving' | 'doorOpen';
export type Direction = 1 | -1 | 0;

/** 某层的接客方向标记：是否有上行/下行呼叫指派给本车。 */
export interface PickupFlags {
  up: boolean;
  down: boolean;
}

export class ElevatorCar {
  readonly id: string;
  readonly capacity: number;
  readonly config: ElevatorConfig;

  state: CarState = 'idle';
  floor: number; // 当前逻辑楼层（运行中保持出发楼层，到达时更新）
  direction: Direction = 0;

  /** 轿内乘客：passengerId -> 目的楼层。 */
  readonly riders = new Map<number, number>();
  /** 待接客楼层 → 方向标记（方向感知停靠：只在与承诺方向同向时才作为停靠点）。 */
  readonly pickups = new Map<number, PickupFlags>();
  /** 已指派但尚未上梯的乘客数（用于派梯负载感知，防止单梯过载）。 */
  pending = 0;

  // 当前运行段（渲染插值 / 指标采集共用）
  segFromFloor = 0;
  segToFloor = 0;
  segStartMs = 0;
  segEndMs = 0;
  segLoad = 0; // 该段运行时的载客数（指标用）

  constructor(config: ElevatorConfig, lobbyFloor: number) {
    this.id = config.id;
    this.capacity = config.capacity;
    this.config = config;
    this.floor = config.initialFloor ?? lobbyFloor;
    this.segFromFloor = this.floor;
    this.segToFloor = this.floor;
  }

  get load(): number {
    return this.riders.size;
  }

  get hasStops(): boolean {
    return this.riders.size > 0 || this.pickups.size > 0;
  }

  /** 登记一层接客任务（方向标记）。 */
  addPickup(floor: number, direction: 1 | -1): void {
    const fl = this.pickups.get(floor) ?? { up: false, down: false };
    if (direction === 1) fl.up = true;
    else fl.down = true;
    this.pickups.set(floor, fl);
  }

  /** 清除某层某方向的接客标记；两个方向都无则删除该层。 */
  clearPickup(floor: number, direction: 1 | -1): void {
    const fl = this.pickups.get(floor);
    if (!fl) return;
    if (direction === 1) fl.up = false;
    else fl.down = false;
    if (!fl.up && !fl.down) this.pickups.delete(floor);
  }

  /**
   * 方向感知选下一停靠楼层（LOOK + 集体控制）：
   * - 同向服务层 = 轿内目的层(同向) + 同向呼叫接客层；取最近者，维持方向；
   * - 同向无服务层 → 反向取最近服务层（端点折返）；
   * - 两侧都无 → 仅本层接客时原地开门，否则 null（空闲）。
   *
   * 反向呼叫的楼层不计入停靠，故不会在正向扫梯时「空停」。
   */
  nextStop(): { floor: number; direction: Direction } | null {
    const here = this.floor;
    const dir = this.direction;
    const riderDests = [...this.riders.values()];

    if (dir === 0) {
      // 空闲：任意方向的最近服务层（轿内目的层 或 任一接客层）
      const all: number[] = [];
      for (const d of riderDests) if (d !== here) all.push(d);
      for (const f of this.pickups.keys()) if (f !== here) all.push(f);
      if (all.length > 0) {
        const t = all.reduce((best, f) => (Math.abs(f - here) < Math.abs(best - here) ? f : best));
        return { floor: t, direction: t > here ? 1 : -1 };
      }
      return this.pickups.has(here) ? { floor: here, direction: 0 } : null;
    }

    const ahead = (f: number, d: Direction) => (d === 1 ? f > here : f < here);
    const collect = (d: Direction): number[] => {
      const out: number[] = [];
      for (const rd of riderDests) if (ahead(rd, d)) out.push(rd);
      for (const [f, fl] of this.pickups) if (ahead(f, d) && (d === 1 ? fl.up : fl.down)) out.push(f);
      return out;
    };

    const same = collect(dir);
    if (same.length > 0) {
      const t = dir === 1 ? Math.min(...same) : Math.max(...same);
      return { floor: t, direction: dir };
    }

    const rev: Direction = dir === 1 ? -1 : 1;
    const revStops = collect(rev);
    if (revStops.length > 0) {
      const t = rev === 1 ? Math.min(...revStops) : Math.max(...revStops);
      return { floor: t, direction: rev };
    }

    // 两侧都无服务层：仅本层接客时原地开门
    return this.pickups.has(here) ? { floor: here, direction: dir } : null;
  }
}
